import { Suspense } from 'react'
import { useSelector } from 'react-redux'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import { useGetMenuQuery } from '@/layout/main-api'
import {
  CHANGE_PASSWORD_PATH,
  selectMustChangePassword,
  selectToken
} from '@/pages/login/authSlice'
import Layout from '@/layout'
import LayoutContent from '@/components/LayoutContent'

const PrivateRoute = () => {
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const mustChangePassword = useSelector(selectMustChangePassword)
  const location = useLocation()
  const { data: menu, isSuccess: menuSuccess } = useGetMenuQuery(undefined, {
    skip: !token || mustChangePassword
  })

  const route =
    menuSuccess && menu.find((item) => item.action === location.pathname)
  const isAccess = String(location.pathname).search(route?.action) !== -1

  // Account still has the admin-generated one-time password: no other pages until it is changed
  if (token && mustChangePassword) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />
  }

  return token 
    ? (
      <Layout>
        {
          isAccess 
            ? <OutletWrapper>
                <Outlet />
              </OutletWrapper>
            : (menuSuccess && (
              <LayoutContent>
                <Typography variant="h6">{t('access_error')}</Typography>
                <Alert variant="standard" severity="error">
                  <AlertTitle>{t('access_denied')}</AlertTitle>
                  <Typography>{t('no_access_to_page')}</Typography>
                </Alert>
              </LayoutContent>
            ))
        }
      </Layout>
    ) : (
      <Navigate to="/login" state={{ from: location }} replace />
    )
}

const OutletWrapper = ({children}) => {
  const location = useLocation()
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[
        location.pathname
      ]}
      onReset={() => {
        window.location.reload()
      }}
    >
      <Suspense 
        key={location.pathname}
        fallback={<LinearProgress />}
      >
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function ErrorFallback({ error, resetErrorBoundary }) {
  const { t } = useTranslation()
  return (
    <LayoutContent>
      <Alert severity="error" sx={{ m: 2 }}>
        <AlertTitle>{t('error')}</AlertTitle>
        <Typography>{error.message}</Typography>
        <Button 
          onClick={resetErrorBoundary} 
          variant="contained" 
          color="primary"
        >{t('reload')}</Button>
      </Alert>
    </LayoutContent>
  );
}

export default PrivateRoute
